import * as z from "zod"

export const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
export const emailSchema = z.string().email("Invalid email address")
export const phoneSchema = z.string().min(1, "Phone number is required")
export const firstNameSchema = z
  .string()
  .min(1, "First name is required")
  .max(100, "First name must be 100 characters or less")
export const lastNameSchema = z
  .string()
  .min(1, "Last name is required")
  .max(100, "Last name must be 100 characters or less")
export const vatNumberSchema = z.string()
export const ianaTimezoneNameSchema = z.string().min(1, "Timezone is required")
export const uuidSchema = z.guid("Invalid UUID")

export type Password = z.infer<typeof passwordSchema>
export type Email = z.infer<typeof emailSchema>
export type Phone = z.infer<typeof phoneSchema>
export type FirstName = z.infer<typeof firstNameSchema>
export type LastName = z.infer<typeof lastNameSchema>
export type VatNumber = z.infer<typeof vatNumberSchema>
export type IanaTimezoneName = z.infer<typeof ianaTimezoneNameSchema>
export type UUID = z.infer<typeof uuidSchema>

export const uploadedFileSchema = z.object({
  id: z.string().optional(),
  bucket: z.string().min(1, "Bucket is required"),
  fullPath: z.string().min(1, "Full path is required"),
  metadata: z.record(z.string(), z.string()).optional(),
  extData: z.record(z.string(), z.string()).optional(),
})

export type UploadedFile = z.infer<typeof uploadedFileSchema>

export const addressSchema = z.object({
  id: z.string().optional(),
  addressLine1: z
    .string()
    .min(1, "Address line 1 is required")
    .max(255, "Address line 1 must be 255 characters or less"),
  addressLine2: z
    .string()
    .max(255, "Address line 2 must be 255 characters or less")
    .optional(),
  city: z
    .string()
    .min(1, "City is required")
    .max(100, "City must be 100 characters or less"),
  state: z.string().max(100, "State must be 100 characters or less").optional(),
  postalCode: z
    .string()
    .min(1, "Postal code is required")
    .max(20, "Postal code must be 20 characters or less"),
  country: z
    .string()
    .min(1, "Country is required")
    .max(100, "Country must be 100 characters or less"),
  extData: z.record(z.string(), z.string()).optional(),
})

export type Address = z.infer<typeof addressSchema>

export const bankingDetailsSchema = z.object({
  id: z.string().optional(),
  bankName: z
    .string()
    .min(1, "Bank name is required")
    .max(255, "Bank name must be 255 characters or less"),
  accountName: z
    .string()
    .min(1, "Account name is required")
    .max(255, "Account name must be 255 characters or less"),
  accountNumber: z
    .string()
    .min(1, "Account number is required")
    .max(100, "Account number must be 100 characters or less"),
  swiftCode: z
    .string()
    .min(1, "SWIFT/BIC/Sort code is required")
    .max(20, "SWIFT/BIC/Sort code must be 20 characters or less"),
  bankAddress: z
    .string()
    .min(1, "Bank address is required")
    .max(500, "Bank address must be 500 characters or less"),
})

export type BankingDetails = z.infer<typeof bankingDetailsSchema>

// Query parameter schemas for tables, lists, and other paginated/filterable content
export const sortingQueryParamSchema = z
  .array(
    z.object({
      id: z.string(),
      desc: z.boolean(),
    }),
  )
  .optional()

export const columnFilterSchema = z.object({
  id: z.string(),
  value: z.any().refine((val) => val !== undefined, "Value is required"),
})

export const columnFiltersQueryParamSchema = z
  .array(columnFilterSchema)
  .optional()

export const paginationQueryParamSchema = z.object({
  page: z.coerce.number<number>().default(1),
  page_size: z.coerce.number<number>().default(20),
})

export const searchQueryParamSchema = z.object({
  q: z.string().optional(), // Global search query
})

// Combined base schema for data table routes
// Using .merge() to combine schemas at the same level, then .extend() to add new fields
export const dataTableBaseSearchSchema = searchQueryParamSchema
  .merge(paginationQueryParamSchema)
  .extend({
    sorting: sortingQueryParamSchema,
    columnFilters: columnFiltersQueryParamSchema,
  })

export type SortingQueryParam = z.infer<typeof sortingQueryParamSchema>
export type ColumnFilter = z.infer<typeof columnFilterSchema>
export type ColumnFiltersQueryParam = z.infer<
  typeof columnFiltersQueryParamSchema
>
export type PaginationQueryParam = z.infer<typeof paginationQueryParamSchema>
export type SearchQueryParam = z.infer<typeof searchQueryParamSchema>
export type DataTableBaseSearch = z.infer<typeof dataTableBaseSearchSchema>
